/**
 * Sprint 22 — per-route chat scope declaration.
 *
 * A route calls `useChatScope({kind, id?, type_slug?, label?}, pageContext?)` to
 * tell the cockpit chat what it's "asking about". Mirrors the useRightRail
 * pattern: the values are read from a ref so a fresh object each render doesn't
 * retrigger; `deps` (useEffect semantics) drive refresh. On unmount the scope
 * resets to workspace, so navigating away from a scoped page never leaves the
 * chat pinned to a stale entity.
 *
 * The declaration is one-way: a page that declares a scope renders identically
 * whether or not a rail is mounted to receive it. №53 (53.0) re-parents the
 * console settings pages under `/app`, where the chat layer is the CoPanel and
 * ChatRailProvider does not mount — there the declaration is a no-op.
 */

import { useEffect, useRef, type DependencyList } from "react";
import { useChatRailOptional, type ChatScope, type PageContext } from "../components/chat/ChatRailContext";

export type { ChatScope, PageContext };

export function useChatScope(
  scope: ChatScope,
  pageContext?: PageContext | null,
  deps: DependencyList = [],
): void {
  const rail = useChatRailOptional();
  const setScope = rail?.setScope;
  const resetScope = rail?.resetScope;
  const ref = useRef<{ scope: ChatScope; pageContext: PageContext | null }>({
    scope,
    pageContext: pageContext ?? null,
  });
  ref.current = { scope, pageContext: pageContext ?? null };

  useEffect(() => {
    if (!setScope || !resetScope) return;
    setScope(ref.current.scope, ref.current.pageContext);
    return () => resetScope();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setScope, resetScope, ...deps]);
}
